import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HStack } from '@terra/ui-styled-system';
import { COMMAND_PRIORITY_EDITOR } from 'lexical';
import { ArrowUp02Icon, AttachmentIcon, SquareIcon } from '@terra/ui-icons';
import { useEffect, useState } from 'react';
import { IconButton } from '../../../../components/icon-button';
import { Tooltip } from '../../../../components/tooltip';
import { IS_EDITOR_EMPTY } from './plugins/OnChangePlugin';
import { useTranslations } from 'next-intl';

interface EditorFooterProps {
  onSubmit: () => void;
  onStop: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  isSubmitDisabled?: boolean;
  allowEmptySubmit?: boolean;
  submitButtonAriaLabel?: string;
  onFileUploadClick?: () => void;
  showFileUpload?: boolean;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  variant?: 'default' | 'copilot';
}

export function EditorFooter({
  onSubmit,
  onStop,
  isLoading,
  isDisabled,
  isSubmitDisabled,
  allowEmptySubmit = false,
  submitButtonAriaLabel = 'Send',
  onFileUploadClick,
  showFileUpload,
  leftContent,
  rightContent,
  variant = 'default',
}: EditorFooterProps) {
  const t = useTranslations('designSystem.chat.fileUpload');
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [editor] = useLexicalComposerContext();
  const shouldShowStopButton = isLoading && (isSubmitDisabled || isEditorEmpty);
  const disabledSendBg = variant === 'copilot' ? 'bg.subtle' : 'bg.muted';
  const isCopilot = variant === 'copilot';

  useEffect(() => {
    const unreg = editor.registerCommand(
      IS_EDITOR_EMPTY,
      ({ isEditorEmpty }) => {
        setIsEditorEmpty(isEditorEmpty);

        return false;
      },
      COMMAND_PRIORITY_EDITOR,
    );

    return () => unreg();
  }, [editor]);

  return (
    <HStack
      width="100%"
      minW={0}
      flexWrap={isCopilot ? 'wrap' : 'nowrap'}
      justifyContent="space-between"
      alignItems="center"
      gap={2}
      pt={0.5}
      aria-label="Editor actions"
    >
      <HStack
        gap={1}
        alignItems="center"
        flex={isCopilot ? '1' : undefined}
        minW={isCopilot ? 0 : undefined}
        flexWrap={isCopilot ? 'wrap' : 'nowrap'}
        flexShrink={isCopilot ? 1 : 0}
      >
        {showFileUpload && onFileUploadClick && (
          <Tooltip title={t('attachFiles')} positioning={{ placement: 'top' }}>
            <IconButton
              variant="ghost"
              aria-label={t('attachFiles')}
              onClick={onFileUploadClick}
              disabled={isLoading || isDisabled}
              _hover={{ color: 'fg.default', bg: 'transparent' }}
              _disabled={{
                color: 'fg.subtle',
                cursor: 'not-allowed',
              }}
              size="xs"
            >
              <AttachmentIcon size={16} />
            </IconButton>
          </Tooltip>
        )}
        {leftContent}
      </HStack>

      <HStack gap={1} alignItems="center" flexShrink={0}>
        {rightContent}
        {shouldShowStopButton ? (
          <IconButton
            variant="solid"
            aria-label="Stop / Abort"
            onClick={onStop}
            bg="fg.default"
            color="bg.default"
            borderRadius="full"
            _hover={{ bg: 'fg.muted' }}
            size={isCopilot ? '3xs' : 'sm'}
            p={0}
          >
            <SquareIcon
              size={isCopilot ? 10 : 14}
              fill="currentColor"
              strokeWidth={0}
            />
          </IconButton>
        ) : (
          <IconButton
            data-tour-id="send-chat-message"
            variant="solid"
            aria-label={submitButtonAriaLabel}
            onClick={onSubmit}
            disabled={
              isSubmitDisabled ||
              (!!isEditorEmpty && !allowEmptySubmit) ||
              isDisabled
            }
            borderRadius="full"
            bg="fg.default"
            color="bg.default"
            _hover={{ bg: 'fg.muted' }}
            _disabled={{
              bg: disabledSendBg,
              color: 'fg.disabled',
              cursor: 'not-allowed',
              opacity: 1,
            }}
            size={isCopilot ? '3xs' : 'sm'}
            p={0}
          >
            {isCopilot ? (
              <ArrowUp02Icon size={12} strokeWidth={1.33} />
            ) : (
              <ArrowUp02Icon size={16} />
            )}
          </IconButton>
        )}
      </HStack>
    </HStack>
  );
}
